import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type TeacherAssignment = {
  stream_id: string;
  subject_id: string | null;
  term_id: string | null;
  stream: { id: string; name: string; class_id: string; school_id: string };
  class: { id: string; name: string; grade: string };
  subject: { id: string; name: string; code: string | null } | null;
};

export type TeacherCtx = {
  loading: boolean;
  staffId: string | null;
  schoolId: string | null;
  schoolName: string | null;
  schoolCurrency: string;
  fullName: string;
  assignments: TeacherAssignment[];
  streams: { id: string; name: string; class_name: string; class_id: string }[];
  subjects: { id: string; name: string; code: string | null }[];
  classTeacherOf: { id: string; name: string; class_name: string }[];
  refresh: () => Promise<void>;
};

export function useTeacherContext(): TeacherCtx {
  const [state, setState] = useState<Omit<TeacherCtx, "refresh">>({
    loading: true,
    staffId: null,
    schoolId: null,
    schoolName: null,
    schoolCurrency: "UGX",
    fullName: "",
    assignments: [],
    streams: [],
    subjects: [],
    classTeacherOf: [],
  });

  const load = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setState((s) => ({ ...s, loading: false })); return; }

    const { data: staff } = await supabase
      .from("staff")
      .select("id, school_id, first_name, last_name, school:schools(name, currency)")
      .eq("user_id", user.id)
      .maybeSingle();

    if (!staff) { setState((s) => ({ ...s, loading: false })); return; }

    const [{ data: assigns }, { data: classTeacher }] = await Promise.all([
      supabase
        .from("staff_assignments")
        .select("stream_id, subject_id, term_id, stream:streams(id,name,class_id,school_id,class:classes(id,name,grade)), subject:subjects_catalog(id,name,code)")
        .eq("staff_id", staff.id),
      supabase
        .from("streams")
        .select("id, name, class:classes(id, name)")
        .eq("class_teacher_id", staff.id),
    ]);

    const rows = (assigns ?? []) as any[];
    const assignments: TeacherAssignment[] = rows.map((r) => ({
      stream_id: r.stream_id,
      subject_id: r.subject_id,
      term_id: r.term_id,
      stream: { id: r.stream?.id, name: r.stream?.name, class_id: r.stream?.class_id, school_id: r.stream?.school_id },
      class: r.stream?.class ?? { id: "", name: "", grade: "" },
      subject: r.subject ?? null,
    }));

    const streamMap = new Map<string, { id: string; name: string; class_name: string; class_id: string }>();
    for (const a of assignments) {
      if (a.stream.id) streamMap.set(a.stream.id, { id: a.stream.id, name: a.stream.name, class_name: a.class.name, class_id: a.stream.class_id });
    }
    const subjectMap = new Map<string, { id: string; name: string; code: string | null }>();
    for (const a of assignments) if (a.subject) subjectMap.set(a.subject.id, a.subject);

    setState({
      loading: false,
      staffId: staff.id,
      schoolId: staff.school_id,
      schoolName: (staff.school as any)?.name ?? null,
      schoolCurrency: (staff.school as any)?.currency ?? "UGX",
      fullName: `${staff.first_name ?? ""} ${staff.last_name ?? ""}`.trim(),
      assignments,
      streams: Array.from(streamMap.values()),
      subjects: Array.from(subjectMap.values()),
      classTeacherOf: (classTeacher ?? []).map((s: any) => ({ id: s.id, name: s.name, class_name: s.class?.name ?? "" })),
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  return { ...state, refresh: load };
}

export function formatStreamLabel(s: { class_name?: string; name?: string } | null | undefined) {
  if (!s) return "—";
  return [s.class_name, s.name].filter(Boolean).join(" · ");
}
